<script lang="ts">
  import CouponHeader from './CouponHeader.svelte'
  import moment from 'moment'
  import { isRelativeDate, textColor, stopPropagation } from '@/utils'
  import Countdown from './Countdown.svelte'
  import bwipjs from 'bwip-js'

  let {
    coupon,
    mode = 'coupon',
    beginRedemption = () => {},
    completeRedemption = () => {},
    confirmRedemption = () => {},
    cancelRedemption = () => {},
    barcodeRenderError = () => {},
    lastRedemption = null,
    showConfirmRedemption = false,
    redemptionInProgress = false,
    isRedeemed = false,
    loaded = true, // Default to true for edit mode
    live = false // Is it being viewed by a customer?
  } = $props()

  let lastRedemptionRelative = $state(null)
  let lastRedemptionTimer = $state(null)

  let expirationRelative = $derived(isRelativeDate(coupon.expiration_date))
  let expiresAt = $derived.by(() => {
    // PublicCoupon will set this
    if (coupon.expires_at) {
      return coupon.expires_at
    }
    // otherwise we're in edit mode
    if (coupon.has_expiration_date) {
      let expiration

      if (expirationRelative) {
        expiration = moment().add(coupon.expiration_date, 'days')
      } else {
        // coupon.expiration_date and expiration_time are strings at first
        // but after binding to flatpickr, they become objects
        // so in either case we'll parse into date then format as string,
        // then parse final result

        let date = moment(coupon.expiration_date, 'MM/DD/YYYY').format('MM/DD/YYYY')
        let time = coupon.expiration_time ?
          moment(coupon.expiration_time, 'HH:mm A').format('HH:mm A') :
          ''

        // console.log('date', date)
        // console.log('time', time)
        expiration = moment(`${date} ${time}`, 'MM/DD/YYYY HH:mm A')
      }

      return expiration.isValid() ? expiration : null
    }
  })

  let googleMapsUrl = $derived(`https://www.google.com/maps/place/${coupon.directions_url}`)
  let hasVisibleLinks = $derived(coupon.show_phone_number ||
    coupon.show_website_url ||
    coupon.show_facebook_url ||
    coupon.show_directions_url
  )
  let isExpired = $derived(!isRedeemed && moment() > moment(new Date(expiresAt)))
  let offerImage = $derived(coupon.offer_image_src || coupon.offer_image_url)
  let redeemable = $derived(loaded && !isRedeemed && !isExpired)

  let redeemedAt = $state(null)
  $effect(() => {
    if (lastRedemption) {
      calculateRedeemedAt()
    }
  })

  function calculateRedeemedAt() {
    if (lastRedemption) {
      redeemedAt = moment(lastRedemption.created_at + 'Z').fromNow()
      window.setTimeout(calculateRedeemedAt, 10000)
    }
  }

  let shouldHideRedemptionCode = $derived(
    coupon.redemption_type === 'barcode' &&
    coupon.barcode_data &&
    coupon.barcode_format !== 'qr'
  )

  // if any of these change, we need to re-render the barcode and set data
  $effect(() => {
    renderBarcode(
      coupon.redemption_type,
      coupon.barcode_format,
      coupon.redemption_code
    )
  })

  const bcids = {
    'code128': 'code128',
    'code39': 'code39',
    'itf14': 'itf14',
    'qr': 'qrcode',
  }

  function renderBarcode(type, format, code) {
    if (type !== 'barcode') { return }

    const bcid = bcids[format.toLowerCase()] || 'code128'
    const scale = format === 'qr' ? 2 : 1

    try {
      // The return value is the canvas element
      bwipjs.toCanvas('barcode-canvas', {
        bcid,                 // Barcode type
        text: String(code),   // Text to encode
        scale,                // scaling factor
        height: 15,           // Bar height, in millimeters
        includetext: true,    // Show human-readable text
        textxalign: 'center', // Always good to set this
      });
      barcodeRenderError('')
    } catch (e) {
      barcodeRenderError(e)
    }
  }
</script>


<div class="Coupon" class:is-expired={isExpired} class:is-redeemed={isRedeemed}>

  {#if isExpired}
    <div class="coupon__expired">
      <div class="coupon__expired_content">
        <div class="coupon__expired_img"></div>
        <hr>
        <div class="coupon__expired_text">This offer has expired.</div>
      </div>
    </div>
  {/if}

  {#if isRedeemed}
    <div class="coupon__redeemed">
      <div class="coupon__redeemed_content">
        <div class="coupon__redeemed_img"></div>
        <hr>
        <div class="coupon__redeemed_text">
          This offer was redeemed {redeemedAt}
        </div>
      </div>
    </div>
  {/if}

  <div class="coupon-mode" class:d-none={mode !== 'coupon'} id="coupon-content">
    <CouponHeader
      text={coupon.offer_header_text}
      backgroundColor={coupon.color}
      color={textColor(coupon.color)} />

    <div class="coupon__image">
      <img src={offerImage} class:d-none="{!offerImage}" alt="Offer" />
      <div class="default-image" class:d-none="{!!offerImage}"></div>
    </div>

    <div class="coupon__offer-text">
      <div class:d-none={!coupon.offer_title}>{coupon.offer_title}</div>
      <div class:d-none={!!coupon.offer_title}>Offer title goes here</div>
    </div>

    <div class="coupon__offer-description">
      <div class:d-none={!coupon.offer_description}>
        {coupon.offer_description}
      </div>
      <div class:d-none={!!coupon.offer_description}>
        Offer description goes here. Best to keep this concise!
      </div>
    </div>

    <div class="coupon__expiration" class:d-none={!coupon.has_expiration_date}>
      <div>This coupon expires in</div>
      <Countdown className="boxes" datetime={expiresAt}></Countdown>
    </div>

    <div onclick={() => beginRedemption()}
         onkeydown={(e) => (e.key === 'Enter' || e.key === ' ') && beginRedemption()}
         role="button"
         tabindex="-1"
         class:disabled={!redeemable}
         class="btn coupon__redeem-button"
         style="background-color: {coupon.color}; color: {textColor(coupon.color)}">

      {#if lastRedemption}
        <div>Redeemed {redeemedAt}</div>
      {:else if coupon.redeem_button_text}
        <div>{coupon.redeem_button_text}</div>
      {:else}
        <div>Redeem Now!</div>
      {/if}

      <div class="coupon__confirm_single_use" class:d-none={!showConfirmRedemption}>
        <p>
          <strong>This coupon can only be used once.</strong>
          <br>
          Are you sure you want to redeem it now?
        </p>
        <button
          type="button"
          class="coupon__redeem-button--confirm"
          onclick={stopPropagation(confirmRedemption)}
          style="background-color: {coupon.color}; color: {textColor(coupon.color)}">
          Yes, Redeem now!
        </button>
        <button
          type="button"
          class="coupon__redeem-button--confirm"
          onclick={stopPropagation(cancelRedemption)}
          style="background-color: {coupon.color}; color: {textColor(coupon.color)}">
          No, Cancel
        </button>
      </div>
    </div>

    <div class="coupon__links flex-row" class:d-none={!hasVisibleLinks} id="coupon-links">
      <a class="cell-1" href="{'tel:'+coupon.phone_number}" target="_blank" class:d-none={!coupon.show_phone_number}>
        <div class="icon icon--coupon-phone"></div>
        <span>Call</span>
      </a>
      <a class="cell-1" href="{coupon.website_url}" target="_blank" class:d-none={!coupon.show_website_url}>
        <div class="icon icon--coupon-website"></div>
        <span>Website</span>
      </a>
      <a class="cell-1" href="{coupon.facebook_url}" target="_blank" class:d-none={!coupon.show_facebook_url}>
        <div class="icon icon--coupon-facebook"></div>
        <span>Facebook</span>
      </a>
      <a class="cell-1" href="{googleMapsUrl}" target="_blank" class:d-none={!coupon.show_directions_url}>
        <div class="icon icon--coupon-directions"></div>
        <span>Directions</span>
      </a>
    </div>
    <div class="coupon__fine-print" id="coupon-fine-print">
      <div class:d-none={!coupon.fine_print}>{coupon.fine_print}</div>
      <div class:d-none={coupon.fine_print || live}>Disclaimers and fine print goes here.</div>
    </div>
  </div>

  <div class="redeem-mode" class:d-none={mode !== 'redeem'} id="coupon-redemption">
    <CouponHeader
      text={coupon.offer_header_text}
      backgroundColor={coupon.color}
      color={textColor(coupon.color)} />

    <div class="coupon__redemption_action">
      <div class:d-none={!coupon.redemption_action}>{coupon.redemption_action}</div>
      <div class:d-none={coupon.redemption_action || live}>Redemption instructions go here.</div>
    </div>

    {#if coupon.redemption_type === 'barcode'}
      <div class="coupon__redemption_barcode">
        <canvas id="barcode-canvas"></canvas>
      </div>
    {/if}
    <div class:d-none={shouldHideRedemptionCode} class="coupon__redemption_code">
      <div class:d-none={!coupon.redemption_code}>{coupon.redemption_code}</div>
      <div class:d-none={!!coupon.redemption_code}>BOGO123</div>
    </div>

    <button
      type="button"
      onclick={() => completeRedemption() }
      class="btn coupon__redeem-button coupon__redeem-button--complete">
      {#if lastRedemption}
        Redeemed {redeemedAt}
      {:else if redemptionInProgress}
        Redeeming...
      {:else}
        Complete Redemption
      {/if}
    </button>
  </div>

</div>

<style lang="scss">
  .d-none {
    display: none !important;
  }
</style>
